import { Component, EventEmitter, Input, Output, OnChanges, ViewChildren, ViewContainerRef, QueryList } from '@angular/core';

import { GenericValue, HisAuthService, HisBaseGlobalsService, HisGlobalDataService, HisValueGroup } from 'hisng';

import { DYNAMIC_FIELDSET_ANCHOR } from 'app/fs/res/common/model/dynamic-content/constants';
import { DataDictionaryUtils } from 'app/fs/res/common/utils/data-dictionary-utils';
import { ProviderManager } from 'app/fs/res/common/utils/provider-manager';

import { DATA_SERVICE_KEY_PUBLICATION } from 'app/fs/res/publication/model/constants';
import { PublicationService } from 'app/fs/res/publication/service/publication.service';
import { PublicationType } from 'app/fs/res/publication/model/publication-type';
import { GlobalIdentifier } from 'app/fs/res/publication/model/global-identifier';
import { Publication } from 'app/fs/res/publication/model/publication';
import { Duplicate } from 'app/fs/res/common/model/duplicate';

const IDENTIFIERS_KEY =
    'CONCAT2(Publication.globalIdentifiers.globalIdentifierType.uniquename, \': \', Publication.globalIdentifiers.identifier)';

@Component({
    selector: 'res-publication-data-tab',
    templateUrl: './publication-data-tab.component.html',
    providers: [ProviderManager.getComponentProvider(PublicationDataTabComponent)],
})
export class PublicationDataTabComponent implements OnChanges {
    @Input() publication: Publication;
    @Input() childPublication: Publication;
    @Input() publicationTypeChildParentMap: {[key: string]: PublicationType[]};
    @Input() changeRequestValueGroup: HisValueGroup;
    @Output() publicationTypeChange: EventEmitter<GenericValue> = new EventEmitter<GenericValue>();
    @Output() navigationLastUrlChange = new EventEmitter<string>();
    @Output() navigateToCreateParentOnNextSave = new EventEmitter<boolean>();
    @Output() navigateToEditParentOnNextSave = new EventEmitter<boolean>();
    @ViewChildren(DYNAMIC_FIELDSET_ANCHOR, { read: ViewContainerRef })
        fieldsetViewContainerList: QueryList<ViewContainerRef>;

    /** (potentielle) Duplikate, die anhand des Identifiers gefunden wurden */
    public duplicates: Duplicate[] = null;
    @Output() duplicatesByTitleChange = new EventEmitter<Duplicate[]>();
    @Output() duplicatesByIdentifierChange = new EventEmitter<Duplicate[]>();

    constructor( public auth: HisAuthService,
        public hisBaseGlobalsService: HisBaseGlobalsService,
        private globalDataService: HisGlobalDataService,
        private publicationService: PublicationService) {
    }

    ngOnChanges() {
        // publication.title is not available yet in ngOnInit() because publication is loaded asyncronously via a web service
        this.checkForDuplicates();
    }

    public onPublicationTypeChange(event: any) {
        this.publicationTypeChange.emit(event);
    }

    public onGlobalIdentifiersChange(event: GlobalIdentifier[]) {
        console.log('global identifiers changed. event =', event);
        this.publication.globalIdentifiers = event;
        this.checkForDuplicates();
    }

    private checkForDuplicates() {
        console.log('Publication duplicates check by identifier: identifiers =', this.publication.globalIdentifiers);

        let identifiersToCheck: GlobalIdentifier[];
        if ( this.publication.globalIdentifiers ) {
            identifiersToCheck = this.publication.globalIdentifiers.filter( gi =>
                gi.globalIdentifierType && gi.identifier && gi.identifier.trim().length > 0
            );
        } else {
            identifiersToCheck = [];
        }
        if ( identifiersToCheck.length === 0 ) {
            console.log('Publication duplicates check by identifier: Nothing to check...');
            this.duplicates = null;
            this.duplicatesByIdentifierChange.emit(this.duplicates);
            return;
        }

        // check for duplicates by identifier
        let attributesMap: Record<string, string[]> = {};
        attributesMap[IDENTIFIERS_KEY] = identifiersToCheck.map(gi => gi.globalIdentifierType.uniquename + ': ' + gi.identifier);

        this.publicationService.checkDuplicatesOnCreate( attributesMap ).subscribe( (dupCheckResult: Publication[]) => {
            this.duplicates = dupCheckResult.filter( dup => !this.publication.id || dup.id !== this.publication.id )
                .map( p => new Duplicate(p.id, p.title, Publication.getDescription(p)) );
            if (this.duplicates?.length > 0) {
                console.log( 'Publication duplicates check by identifier: Found potential duplicates:', this.duplicates );
            } else {
                console.log( 'Publication duplicates check by identifier: No duplicates found' );
            }
            this.duplicatesByIdentifierChange.emit(this.duplicates);
        });
    }

    public onBeforeNavigate() {
        this.navigationLastUrlChange.emit( window.location.href );

        if (this.publication) {
            this.globalDataService.setData(DATA_SERVICE_KEY_PUBLICATION, this.publication);
        }
    }

    public getDdKey(source: string) {
        return DataDictionaryUtils.dDKeyPublicationType(this.publication.publicationType, source);
    }

    /* callback to set last window location href */
    public onNavigationLastUrlChange( lastWindowLocation: string ) {
        this.navigationLastUrlChange.emit( lastWindowLocation );
    }

    public onDuplicatesByTitleChange( duplicates: Duplicate[] ) {
        console.log('publication-data-tab: duplicates by title changed to', duplicates);
        this.duplicatesByTitleChange.emit(duplicates);
    }
}
